import { isFilled } from "@prismicio/client";
import { PrismicLink, PrismicRichText } from "@prismicio/react";

/**
 * @typedef {import("@prismicio/client").Content.BannerCtaSlice} BannerCtaSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<BannerCtaSlice>} BannerCtaProps
 * @type {import("react").FC<BannerCtaProps>}
 */
const BannerCta = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="framing padding-x padding-y-sm text-black"
    >
      <div className={`${slice.variation === "default" ? "bg-blue" : "bg-sky-blue"} overflow-hidden text-white flex flex-col md:flex-row gap-4 md:gap-16 py-4 px-6 items-center justify-between rounded-2xl`}>
        <div className="gap-6 flex items-center">
          {slice.variation === "default" && <svg width="90" height="96" viewBox="0 0 90 96" fill="none" xmlns="http://www.w3.org/2000/svg" className="hidden lg:block">
            <path d="M40.9932 2.86058C38.4548 -0.953525 32.1601 -0.953525 29.6225 2.86058L9.41236 33.2018C-6.84347 59.4125 0.962535 76.1878 10.3769 85.6506C17.2528 92.5507 26.2839 96 35.3078 96C44.3317 96 53.3628 92.5499 60.2396 85.6506C73.67 72.1538 73.957 50.9178 61.0878 33.0094L40.9932 2.86058Z" fill="#57AEF4" />
            <g clipPath="url(#clip0_168_235)">
              <path d="M76.7994 78.7803C76.7994 78.7803 76.6231 78.9762 76.5643 79.0741C64.77 90.555 45.9422 90.457 34.2655 78.7803C22.53 67.0447 22.53 48.0015 34.2655 36.266C45.6484 24.8831 63.8884 24.5109 75.7023 35.1296L76.7994 36.2072C88.5545 47.9623 88.5545 67.0447 76.7994 78.7803Z" fill="white" />
              <path d="M37.9688 39.9284C47.3729 30.5243 62.4194 30.2304 72.1958 39.0271L73.1166 39.9088C82.2856 49.0777 82.795 63.6345 74.6448 73.4108L88.7901 87.5561C89.6717 88.4377 89.6717 89.868 88.7901 90.7496C87.9084 91.6312 86.4782 91.6312 85.5966 90.7496L71.4513 76.6043C61.675 84.7545 47.1378 84.2451 37.9688 75.0761C28.2513 65.3782 28.2513 49.6459 37.9688 39.9284ZM69.1786 42.3969C61.1852 35.2067 48.8815 35.4418 41.1819 43.1414C33.2276 51.0957 33.2276 63.948 41.1819 71.9022C49.0578 79.7782 61.8121 79.8369 69.7664 72.0982C69.8252 72.0394 69.8839 71.961 69.9427 71.9022C77.897 63.948 77.897 51.0761 69.9427 43.1218L69.1786 42.3969Z" fill="#CBD8EE" />
            </g>
            <path d="M63.7603 55.5444L56.0518 47.3703C55.9317 47.2433 55.7411 47.2434 55.6209 47.3703L47.888 55.5704V67.9857C47.888 68.1632 48.021 68.3042 48.1883 68.3042H53.1312C53.1834 68.3043 53.2346 68.3093 53.2847 68.3172H58.3635C58.4137 68.3093 58.4648 68.3043 58.5171 68.3042H63.4599C63.6273 68.3042 63.7603 68.1632 63.7603 67.9857V55.5444ZM65.8477 67.9857C65.8477 69.3857 64.7802 70.5178 63.4599 70.5178H61.3914C61.3422 70.5254 61.2918 70.5308 61.2406 70.5308H51.3427C51.2919 70.5308 51.242 70.5253 51.1932 70.5178H48.1883C46.8681 70.5178 45.8005 69.3857 45.8005 67.9857V57.7839L44.8831 58.7567C44.4756 59.1888 43.8148 59.1888 43.4072 58.7567C42.9996 58.3245 42.9997 57.6239 43.4072 57.1916L54.1437 45.8052C55.079 44.8134 56.5922 44.8136 57.5277 45.8052L68.2655 57.1916C68.6727 57.6239 68.6728 58.3246 68.2655 58.7567C67.858 59.1888 67.1972 59.1886 66.7896 58.7567L65.8477 57.758V67.9857Z" fill="#1A386A" />
            <defs>
              <clipPath id="clip0_168_235">
                <rect width="63.987" height="63.9869" fill="white" transform="translate(25.4697 27.4288)" />
              </clipPath>
            </defs>
          </svg>}
          {isFilled.richText(slice.primary.text) && (
            <div className="relative z-10">
              <PrismicRichText
                field={slice.primary.text}
                components={{
                  heading2: ({ children }) => <h2 className="h2 font-semibold">{children}</h2>,
                  paragraph: ({ children }) => <p className={`${slice.variation === "default" ? "p" : "h3 font-semibold"}`}>{children}</p>,
                }}
              />
            </div>
          )}
        </div>
        {isFilled.link(slice.primary.cta) &&
          slice.primary.cta.variant === "Normal" && (
            <div className="min-w-60 flex justify-end w-full md:w-auto">
              <PrismicLink field={slice.primary.cta} className="cta cta-white cta-text w-full md:w-auto">
                {slice.primary.cta.text}
              </PrismicLink>
            </div>
          )}

        {isFilled.link(slice.primary.cta) &&
          slice.primary.cta.variant === "Téléphone" && (
            <div className="min-w-60 flex justify-end w-full md:w-auto">
              <PrismicLink field={slice.primary.cta} className="flex gap-4 items-center cta cta-white cta-text w-full md:w-auto">
                <svg width="20" height="20" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.8185 1.2213C7.43276 0.298446 6.4269 -0.189836 5.46987 0.0689534L5.20132 0.142196C2.04702 1.00157 -0.648292 4.05821 0.137841 7.77891C1.94936 16.3238 8.673 23.0475 17.2179 24.859C20.9435 25.65 23.9953 22.9498 24.8546 19.7955L24.9279 19.527C25.1916 18.565 24.6984 17.5592 23.7804 17.1783L19.0294 15.2008C18.2238 14.8639 17.2912 15.0983 16.7345 15.777L14.8498 18.0816C11.4171 16.3775 8.65347 13.526 7.06655 10.025L9.22475 8.2672C9.90347 7.71544 10.133 6.78282 9.80093 5.97227L7.8185 1.2213Z" fill="#57AEF4" />
                </svg>
                {slice.primary.cta.text}
              </PrismicLink>
            </div>
          )}
      </div>
    </section>
  );
};

export default BannerCta;
